<template>
  <div class="activity sticky-wrapper">
    <section class="activity property-booking-block" :class="{'property-booking-block--selected': selectedRate?.ref && !isExpanded, 'property-booking-block--expanded': isExpanded  }">
      <button v-if="isExpanded" class="color-blue  btn-toggle-visability btn-toggle-visability--hide" @click="toggleExpand">
        {{t('Hide details')}}
      </button>
      <div class="property-booking-block__header pl-4" :class="{'property-booking-block__header--selected-rate': selectedRate?.ref}">
        <template v-if="property">
          <div class="text-caption price-from__header">
            <span>
              {{t('Price from')}}
            </span>
          </div>
          <div class="price-from__value pt-1">
            <!--TODO: change $ to dynamic currency sign -->
            <span class="text-title-2 fw-bold">{{property.price_from?.formatted}}</span>
          </div>
        </template>
      </div>

      <div class="mobile-no-tablet-only"  v-if="(searchStore.formattedDates[0] && searchStore.formattedDates[1] && !selectedRate?.ref) || (selectedRate?.ref && isExpanded)">
        <PropertyCriteriaMobilePreview
            :hasEdit="isExpanded"
            @edit="isBookingOverlayVisible = true"
        />
      </div>
      <!-- This supplier returns no availability at all until a pickup point is chosen, so the
           pickup selector must gate the calendar rather than sit alongside it. -->
      <div v-if="pickupRequired && pickups.length" class="pickup-selection">
        <label class="pickup-selection__label text-caption" for="activity-pickup">
          {{t('Pickup point')}}
        </label>
        <select
            id="activity-pickup"
            class="pickup-selection__select text-body-1"
            v-model="selectedPickupId"
            aria-label="Select pickup point"
            @change="onPickupSelected">
          <option :value="null" disabled>{{t('Select a pickup point')}}</option>
          <option v-for="p in pickups" :key="p.id" :value="p.id">{{p.name}}</option>
        </select>
      </div>

      <button
          ref="datesTrigger"
          :class="['dates-selection', { 'not-selected': !searchStore.activitySearchDate }]"
          tabindex="0"
          aria-label="Select dates"
          :disabled="pickupRequired && !selectedPickupId"
          @click="togglePanel">
        <div class="dates-selection__date">
          <div class="dates-selection__date-label text-caption">{{t('Date')}}</div>
          <div class="dates-selection__date-value text-body-1">
            {{ pickupRequired && !selectedPickupId ? t('Select a pickup point first') : (searchStore.activitySearchDate || '-') }}
          </div>
        </div>
      </button>
      <Popover ref="datesPanel" class="no-arrow" @show="onCalendarShow">
        <SingleCalendar v-if="availableDates.length" @date-changed="onDateSelected"/>
        <div v-else class="dates-empty text-body-2">
          {{ datesLoading ? t('Loading available dates...') : t('No available dates for this activity') }}
        </div>
      </Popover>
      <button
          class="btn btn-primary checkout-button"
          :class="{'loading': isBookingLoading,  'disabled not-selected': !selectedRate?.ref, 'selected': selectedRate?.ref && !isExpanded}"
          :disabled="isBookingLoading || !isBookingLoading && !selectedRate?.ref"
          v-if="isBookDirectBtn"
          aria-label="Book"
          @click="book">
         <template v-if="!isBookingLoading">
           {{selectedRate?.ref ? t(`${$siteConfig?.features?.bookNowCta ? 'Book Now' : 'Book Direct'}`) : t('Select a room to continue')}} <span class="icon icon-chevron-right desktop-only"/>
         </template>
          <template v-else>
            Loading... <span class="icon icon-loading"/> <!-- Assume you have a loading icon -->
          </template>
      </button>
      <button v-else :disabled="!selectedActivityDate" class="btn btn-primary checkout-button desktop-only" aria-label="Check availability" @click="checkAvailability">
        {{ (packages.length || loadingPackeges) ? t('Update search') : t('Check availability')}} <span class="icon icon-chevron-right"/>
      </button>
      <!-- Its own label says "Select a date", so it is shown precisely when no date is set — yet
           it used to call checkAvailability, which then fetched /dates/null and 404'd. Open the
           calendar the label promises; only re-query once a date actually exists. -->
      <button v-if="!selectedRate?.ref" class="btn btn-primary checkout-button checkout-button--select-date mobile-only" :aria-label="!selectedActivityDate ? 'Select a date' : 'Update search'" @click="selectedActivityDate ? checkAvailability() : togglePanel($event)">
        {{ !selectedActivityDate ? t('Select a date') : t('Update search')}}
      </button>
    </section>
  </div>
</template>

<script setup lang="ts">

import Popover from 'primevue/popover';
import {useActivityPropertyStore} from '~/store/activityProperty'
import {useSearchStore} from '~/store/search'
import dayjs from '~/utils/dayjs'
import {useBookingStore} from '~/store/booking'
import {scrollToElement} from '~/utils/common'
import PropertyCriteriaMobilePreview from "~/components/PropertyPage/PropertyCriteriaMobilePreview.vue";
import SingleCalendar from "~/components/SingleCalendar.vue";
const localePath = useLocalePath()
const { t } = useI18n()
//stores
const propertyStore = useActivityPropertyStore()
const bookingStore = useBookingStore()
const searchStore = useSearchStore()
const property = propertyStore.property
const router = useRouter()
const emit = defineEmits(['onAvailabilityUpdate'])
const packages = computed(() => propertyStore.packages)
const datesPanel = ref();
const datesTrigger = ref();
const datesCalendar = ref();
const visitorsPanel = ref();
const isVisitorsPanelVisible = ref(false)
const isBookingLoading = ref(false)
const isBookingOverlayVisible = ref(false)
const isExpanded = ref(false);
const selectedActivityDate = computed(() => searchStore.activitySearchDate);
const availableDates = computed(() => searchStore.activityAvailableDates);
const pickupRequired = computed(() => searchStore.activityPickupRequired);
const pickups = computed(() => searchStore.activityPickups);
const selectedPickupId = computed({
  get: () => searchStore.activitySelectedPickupId,
  set: (v: any) => { searchStore.activitySelectedPickupId = v }
});

const searchAvailableDates = (date:String) => {
  // Pass the chosen pickup when the product needs one — without it the supplier returns
  // no dates at all and the calendar cannot open.
  const params: any = {start_date: date}
  if (searchStore.activitySelectedPickupId) {
    params.pickup_id = searchStore.activitySelectedPickupId
  }
  return searchStore.searchActivitiesDates({params, id: propertyStore.property?.id})
    .catch((e: any) => {
      // Log it. Silently swallowing here is why a dead calendar went unnoticed:
      // availableDates stayed empty, the Popover's v-if hid it, and the button no-opped.
      console.error('[activity] failed to load available dates', 'status=', e?.status || e?.statusCode, 'msg=', e?.message, 'data=', JSON.stringify(e?.data||{}).slice(0,200))
      // Swallow transient API errors (503s, network) — the UI already degrades gracefully
      // with an empty availableDates list. Surfacing these would spam Sentry with noise.
      // NOTE: "pickup required" is NOT routed here — the store converts it into
      // activityPickupRequired so the UI can prompt instead of showing an empty calendar.
    })
}

const onPickupSelected = () => {
  const startDate = dayjs(new Date()).format('YYYY-MM-DD')
  searchAvailableDates(startDate)
}

const loadingPackeges = ref(false)
const datesLoading = ref(false)
onMounted(async () => {
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('resize', resizeListener);
  // Must run BEFORE the dates call: the pickup, once chosen, is passed as a parameter to it.
  await searchStore.setActivityPickupRequirement(
    !!propertyStore.property?.is_pickup_required,
    propertyStore.property?.id
  )
  // Pre-load available dates starting from today+2 days (default travel date)
  const defaultStartDate = dayjs(new Date()).format('YYYY-MM-DD')
  await searchAvailableDates(defaultStartDate);
  // Only fall back to nagging for a date when we could not produce a price ourselves.
  const preloaded = await preloadPackages();
  if (!preloaded) autoOpenCalendar();
});

// Packages used to load only after the visitor picked a date by hand — `checkAvailability` had no
// caller but `onDateSelected` and the two buttons — so an arriving visitor met an empty booking
// block reading "Choose a date to get started". That is an extra required action standing between
// a landing and any visible price, and on 2026-09-02 not one of the 57 visitors arriving from the
// paid GTTD campaign took it: 83 landings, zero calls to the date-availability endpoint, zero
// bookings on 59 clicks. Load today up front instead, and fall back to tomorrow when today is sold
// out, so the block arrives with a real bookable price already on it.
async function preloadPackages(): Promise<boolean> {
  // Never overwrite a choice already made — a date carried in from search or a shared link.
  if (selectedActivityDate.value) return false
  // A pickup-gated product returns no availability at all until a pickup is chosen, so there is
  // nothing to preload and guessing a date would only produce a misleading empty state.
  if (pickupRequired.value && !selectedPickupId.value) return false
  if (!propertyStore.property?.id) return false

  // `dates` comes back as [{date: 'YYYY-MM-DD', is_bookable, available_units}]. Asking for a day
  // the supplier did not offer is a guaranteed 422 (ETL 3101), so skip it rather than spend a
  // request on it. An empty list means the dates call itself failed — try anyway, it is one call.
  const bookable = new Set(
    (availableDates.value || []).filter((d: any) => d?.is_bookable !== false).map((d: any) => d?.date)
  )
  const candidates = [dayjs(), dayjs().add(1, 'day')]
    .map((d: any) => d.format('YYYY-MM-DD'))
    .filter((d: string) => !bookable.size || bookable.has(d))

  for (const date of candidates) {
    try {
      const response: any = await searchStore.searchActivitiesPackages({id: propertyStore.property.id, date})
      const found = response?.packages || []
      // Sold out for that day: keep the date unset and try tomorrow.
      if (!found.length) continue
      searchStore.setActivitySearchDate(date)
      propertyStore.setPackages(found)
      propertyStore.setPackagesChecked(true)
      emit('onAvailabilityUpdate')
      // Loading the packages silently was only half a fix. On a phone the package list renders
      // below the fold, so the visitor saw an unchanged screen and never learned a bookable price
      // had appeared: 46 paid visitors on 2026-09-03 loaded real packages (median 5.2KB payload,
      // none empty) and not one clicked Book now, while organic — which reaches packages through
      // onDateSelected, and therefore through scrollToEntranceTicket — converted at ~44%.
      // Desktop keeps the sticky block in view, so only narrow viewports need the scroll; the
      // 768px threshold matches useMobileDialog.ts.
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        scrollToEntranceTicket()
      }
      return true
    } catch (e: any) {
      // A sold-out day answers 422 (ETL 3101 "no available packages") rather than an empty list,
      // so this is the ordinary path to the next candidate, not an error worth surfacing.
      console.error('[activity] preload packages failed', 'date=', date, 'status=', e?.status || e?.statusCode)
    }
  }
  return false
}

// Picking a date is the first thing every visitor has to do, and nothing else on the page works
// until they have. Open the calendar for them on arrival rather than making them find the field.
function autoOpenCalendar() {
  // Never fight a state the user is already in:
  //  - a date carried in from search//a shared link means the choice is already made
  //  - a pickup-gated product has no dates until a pickup is chosen, so the panel would be empty
  //  - no dates loaded means we would open onto "No available dates" and look broken
  if (selectedActivityDate.value) return
  if (pickupRequired.value && !selectedPickupId.value) return
  if (!availableDates.value?.length) return

  nextTick(() => {
    const anchor = datesTrigger.value
    if (!anchor || datesPanel.value?.visible) return
    // Only auto-open once the trigger is actually on screen. On mobile the booking block sits
    // below the fold, and a popover anchored off-screen either lands somewhere absurd or drags
    // the viewport away from the gallery the visitor is still looking at.
    const box = anchor.getBoundingClientRect?.()
    if (!box || box.bottom <= 0 || box.top >= (window.innerHeight || 0)) return
    // Popover.show() reads event.currentTarget for its outside-click anchor and takes the
    // positioning target as the second argument — see togglePanel for why both must be the
    // element itself rather than the live event.
    datesPanel.value?.show({ currentTarget: anchor, target: anchor }, anchor)
  })
}

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('resize', resizeListener);
  propertyStore.clearSelectedRate()
  isExpanded.value = false
  isBookingLoading.value = false
});

//computed
const isBookDirectBtn = computed({
  get() {
    return propertyStore.isBookDirectBtn
  },
  set(newValue: boolean) {
    propertyStore.setBookDirectBtnVisible(newValue)
  }
})

const dates = computed({
  get() {
    return searchStore.dates.map((date:any) => date ? new Date(date) : null)
  },
  set(newValue) {
    searchStore.setDates(newValue.map((date:any) => date ? dayjs(date).format('YYYY-MM-DD') : null))
    searchStore.setFormattedDates(newValue.map((date:any) => date ? dayjs(date).format('ddd, DD MMM') : null))
  }
})
const selectedRate = computed(() => propertyStore.selectedRate)
const bookingPeriod = computed(() => {
  if (dates.value[0] && dates.value[1]) {
    return dayjs(dates.value[1]).diff(dayjs(dates.value[0]), 'days')
  }
  return 0
})

const surchargesSum = computed(() => {
  const rateSum = selectedRate?.value?.surcharges?.reduce((acc: any, surcharge: any) => {
    return surcharge.is_mandatory ? acc + parseFloat(surcharge.price.converted.amount) : acc
  }, 0) || 0

  return rateSum.toFixed(2)
})

const taxesSum = computed(() => {
  const rateSum = selectedRate?.value?.taxes_and_fees?.reduce((acc: any, tax: any) => {
    return tax.is_mandatory ? acc + parseFloat(tax.price.converted.amount) : acc
  }, 0) || 0

  return rateSum.toFixed(2)
})

const allMandatoryAdditionalFees = computed(() => {
  const result = Number(surchargesSum.value) + Number(taxesSum.value)
  return result?.toFixed(2)
})


const priceFrom = computed(() => {
  if (propertyStore?.rooms?.length) {
    const cheapestRate = findCheapestRate(propertyStore.rooms)
    return countPricePerNight(cheapestRate, bookingPeriod.value)
  }
  return ''
})

const calendarMinPrice = computed(() => {
  if (!selectedRate?.value?.ref) return null
  // Symbol from the rate's own converted currency — a hardcoded "$" next to a session-currency
  // amount mislabels every non-USD session (and a bare "$" is itself ambiguous to crawlers).
  const base = selectedRate.value.base_price?.converted ?? selectedRate.value.base_price
  return currencySymbol(base?.currency || 'USD') + Number(base?.amount).toFixed()
})

const criteriaWatcher = computed(() => ({
  adults: searchStore.criteria.adults,
  children: searchStore.criteria.children,
  ages: searchStore.criteria.ages,
}));


//methods
// Function to toggle the expanded state
function resizeListener(){
  isExpanded.value = false;
};

function toggleExpand() {
  isExpanded.value = !isExpanded.value;
}

function findCheapestRate(hotelRooms: any) {
  if (!hotelRooms.length) return null

  const _allRates = hotelRooms.map((room: any) => room.rates).flat()

  let cheapestRate = _allRates.reduce((minRate: any, rate: any) => {
    return +rate.total_price.converted.amount < +minRate.total_price.converted.amount ? rate : minRate
  }, { total_price: { converted: { amount: Number.MAX_VALUE } } })

  return cheapestRate
}

function countPricePerNight(rate: any, nights: number) {
  //NOTE: maybe should use total_price according to new low
  return (+rate.base_price.amount / nights)?.toFixed(2)
}

async function togglePanel(event: any) {
  // Grab the anchor BEFORE any await: the browser nulls event.currentTarget once the handler
  // returns, and Popover.show() derives both its positioning target and its outside-click
  // anchor from it. Awaiting first left the panel anchored to null, so the click that opened
  // it immediately counted as an outside click and closed it again — the button looked dead
  // on the first press and only worked on the second.
  const anchor = event?.currentTarget || event?.target
  // The calendar used to be unreachable whenever the on-mount fetch had not populated
  // availableDates (race, transient error, or the component mounting before the property
  // store was ready). Retry on demand so the button is never a dead end.
  if (!availableDates.value?.length && !datesLoading.value) {
    datesLoading.value = true
    try {
      await searchAvailableDates(dayjs(new Date()).format('YYYY-MM-DD'))
    } finally {
      datesLoading.value = false
    }
    await nextTick()
  }
  datesPanel.value?.toggle(event, anchor);
}

function handleScroll() {
  if (datesPanel?.value?.visible) {
    datesPanel?.value?.alignOverlay();
  }

  if (visitorsPanel?.value?.visible) {
    visitorsPanel?.value?.alignOverlay();
  }
}

const showDatesPanel = () => {
  datesPanel.value.show();
}
function onCalendarShow() {
  setTimeout(() => {
    const selectedElement = datesCalendar.value?.$refs?.container.querySelector('.p-highlight')
    if (selectedElement) {
      selectedElement.focus()
    } else {
      datesCalendar.value?.$refs?.container?.querySelector('.p-datepicker-today > span')?.focus()
    }
  })
}
function toggleVisitorsPanel(event: any) {
  visitorsPanel.value?.toggle(event);
  isVisitorsPanelVisible.value = true
}

function onDateSelected(newDates: any) {
  if (newDates) {
    searchStore.setActivitySearchDate(newDates)
    propertyStore.setPackagesChecked(false)
    datesPanel.value.hide()
    nextTick(() => checkAvailability())
  }
}

function checkAvailability() {
  loadingPackeges.value = true
  propertyStore.setPackagesLoading(true)
  propertyStore.setPackages([])
  searchStore.searchActivitiesPackages({id: property.id, date: selectedActivityDate.value}).then((response: any) => {
    propertyStore.setPackages(response?.packages || [])
    scrollToEntranceTicket()
  }).catch((e: any) => {
    // A date with nothing left comes back as a 422 (ETL 3101 "no available packages"), not as
    // an empty list. This promise had no .catch at all, so the rejection was swallowed by the
    // browser and the page kept rendering "Choose a date to get started" — the one instruction
    // the user had already followed. Land on an explicit empty state instead.
    console.error('[activity] failed to load packages', 'status=', e?.status || e?.statusCode, 'data=', JSON.stringify(e?.data || {}).slice(0, 200))
    propertyStore.setPackages([])
    scrollToEntranceTicket()
  }).finally(() => {
    loadingPackeges.value = false
    propertyStore.setPackagesLoading(false)
    propertyStore.setPackagesChecked(true)
  })
  emit('onAvailabilityUpdate')
}

function scrollToEntranceTicket() {
  nextTick(() => {
    scrollToElement('#entranceTicket', -100)
  });
}

function resetRates() {
  isBookDirectBtn.value = false
  propertyStore.clearSelectedRate()
}

async function book() {
  const _selectedRate =  JSON.parse(JSON.stringify(selectedRate.value));

  if (!_selectedRate?.ref)  {
    scrollToEntranceTicket()
    return
  }
  isBookingLoading.value = true;

  const { ref } = _selectedRate;

  let productData = reactive({
    type: 'hotel',
    room_rates: [{
      quantity: searchStore.quantity,
      ref
    }]
  });

  try {
    const response: any = await bookingStore.createExpressOrder(productData);
    const { uid } = response;
    const ONE_DAY = 1000 * 60 * 60 * 24;
    localStorage.removeItem('expressOrderUidExpiry');
    setWithExpiry('expressOrderUidExpiry', uid, ONE_DAY)
    await router.push(localePath('/checkout'))
  } catch (error) {
    //TODO: need to do error handling
    console.log('express order error', error)
    isBookingLoading.value = false;
  }
}

// helpers
function setWithExpiry(key: any, value: any, ttl: any) {
  const now = new Date();
  // `item` is an object which contains the original value as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}
function arraysEqual(a: any, b: any) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}



//watchers
watch(criteriaWatcher, (newValues, oldValues) => {
  if (newValues.adults !== oldValues.adults || newValues.children !== oldValues.children || !arraysEqual(newValues.ages, oldValues.ages)) {
    resetRates()
  }
});

defineExpose({
  showDatesPanel
})

</script>

<style lang="scss" scoped>
.sticky-wrapper {
  position: sticky;
  top: 8px;
  z-index: 2;
  padding-bottom: 24px;

  @media screen and (max-width: 768px){
    top: 0;
  }
}
.activity.property-booking-block {
  background-color: var(--light-grey-transparent);
  border-radius: 10px 10px;
  padding: 7px 12px 12px;
  margin-top: -144px;
  transition: height 0.3s ease;


  //tablet
  @media screen and (max-width: 1024px) and (min-width: 769px){
    margin-top: -154px;
    padding: 12px;
  }

  //mobile
  @media screen and (max-width: 768px){
    transition: height 0.3s ease;
  }

  &--expanded {
    height: 356px;

    .mobile-hidden {
      display: flex !important;
    }

    .checkout-button {
      margin-top: 24px;
    }


    .prices-info {

      .prices-info__item {
        &.total {
          @media screen and (max-width: 768px) {
            border-top: 1px solid var(--grey-border) !important;
            padding-top: 8px !important;
          }
        }

        .prices-info__item-value {
          @media screen and (max-width: 768px) {
            font-size: 16px !important;
          }
        }
      }

      //mobile
      @media screen and (max-width: 768px) {
        max-width: 100% !important;
        padding-top: 16px !important;
        gap: 8px !important;
      }
    }

  }

  &--selected {
    //mobile
    @media screen and (max-width: 768px) {
      height: 112px;
      display: flex;
      justify-content: space-between;
    }
  }

  .btn-toggle-visability {

    &:hover {
      text-decoration: underline;
    }

    &--hide {
      margin-bottom: 17px;
      font-size: 15px;
    }
  }


  .property-booking-block__header {
    min-height: 56px;

    &--selected-rate {
      //mobile
      @media screen and (max-width: 768px) {
        display: none;
      }

    }

    .transparent {
      opacity: 0;
    }

    @media screen and (max-width: 1024px) and (min-width: 769px){
      padding-left: 0;
    }
  }

  $inputHeight: 56px;

  .pickup-selection {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;

    .pickup-selection__label {
      opacity: 0.7;
    }

    .pickup-selection__select {
      width: 100%;
      padding: 8px;
      border: 1px solid var(--surface-border, #ddd);
      border-radius: 6px;
      background: transparent;
    }
  }

.dates-selection {
    display: flex;
    width: 100%;
    margin-top: 19px;
    border: 2px solid var(--grey);
    border-radius: 10px;
    background: var(--white);
    height: $inputHeight;
    text-align: left;
    // A <button> defaults to cursor:default, so this field gave no hint it was clickable.
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }

    &.not-selected {
      border: 2px solid var(--grey);
    }

    &.focus, &:focus, &:focus-visible, &:hover {
      border: 2px solid var(--black);
      outline-color: var(--black);
    }


    &__date-value {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media screen and (max-width: 1024px) and (min-width: 769px){
      margin-top: 32px;
    }

    .dates-selection__date {
      padding: 6px 16px;
      flex: 1;

      .dates-selection__date-label {
        color: var(--dark-grey);

        @media screen and (max-width: 1024px) {
          max-width: 85px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .visitors-selection {
    width: 100%;
    margin-top: 12px;
    border: 2px solid var(--grey);
    border-radius: 10px;
    padding: 7px 16px;
    background: var(--white);
    text-align: left;
    display: flex;
    align-items: center;
    height: $inputHeight;

    &.focus, &:focus, &:focus-visible, &:hover {
      border: 2px solid var(--black);
      outline-color: var(--black);
    }

    @media screen and (max-width: 1024px) and (min-width: 769px){
      margin-top: 8px;
    }

    .visitors-selection__label {
      color: var(--dark-grey);
    }
    .icon {
      font-size: 24px;
    }
  }

  .prices-info {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .mobile-hidden {
      @media screen and (max-width: 768px) {
        display: none;
      }
    }

    .color-blue {
      margin-top: 4px;
      width: fit-content;
      white-space:  nowrap;
    }

    .prices-info__item {
      display: flex;


      &.mobile-hidden {
        @media screen and (max-width: 768px) {
          display: none;
        }
      }

      .prices-info__item-label {
        flex: 1;
        font-size: 16px;
        font-weight: 500;

        .selected-room-name {
          white-space: nowrap;
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        @media screen and (max-width: 768px) {
          line-height: 24px;
        }

        .text-caption {
          margin-top: 1px;
          line-height: 16px;
        }
      }

      .prices-info__item-value {
        font-size: 16px;
        font-weight: 500;
        line-height: 150%;
      }
      &.total {
        border-top: 1px solid var(--grey-border);
        padding-top: 8px;

        @media screen and (max-width: 768px) {
          border-top: none;
          padding-top: 0;
        }

        .prices-info__item-label, .prices-info__item-value {
          font-weight: 600;
        }

        .prices-info__item-value {
          @media screen and (max-width: 768px) {
            color: var(--black);
            font-size: 20px;
            font-style: normal;
            font-weight: 600;
            line-height: 24px; /* 120% */

          }
        }
      }
    }

    //mobile
    @media screen and (max-width: 768px) {
      padding-top: 0;
      gap: 0;
      max-width: 63%;
    }
  }
}



.price-from {
  &__header {
    //here could be value for header
  }
  &__value {

    .text-title-2 {
      color: var(--black);
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }

    &.pt-1 {
      padding-top: 6px;

      @media screen and (max-width: 1024px) and (min-width: 769px) {
        padding-top: 2px;
      }
    }
    > span {

      @media screen and (max-width: 1024px) and (min-width: 769px){
        font-size: 24px;
        padding-top: 0;
        margin-top: 0;
      }
    }

    .per-night-text {
      margin-left: 3px;

      @media screen and (max-width: 1024px) and (min-width: 769px) {
        font-size: 14px;
        font-weight: 400;
      }
    }

  }
}

.dates-calendar :global(.p-datepicker) {
  border: none;
}
.checkout-button {
  width: 100%;
  height: 48px;
  margin-top: 16px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  margin-bottom: 12px;

  &.disabled {
    color: var(--dark-grey);
    background-color: var(--grey-border);
  }

  &.not-selected {
    @media screen and (max-width: 768px) {
      display: none;
    }
  }
  &.selected {
    @media screen and (max-width: 768px) {
      max-width: none;
      width: auto;
      flex-shrink: 0;
      height: 44px;
      margin-top: 0;
      padding: 0 24px;
    }
  }

  &--select-date {
    @media screen and (max-width: 768px) {
      max-width: 100%;
    }
  }

  .icon {
    font-size: 24px;
  }
}
@media screen and (max-width: 768px) {
  .sticky-wrapper {
    padding-bottom: 0;
    background-color: var(--white);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.12);
  }
  .property-booking-block {
    margin-top: 0;
    padding: 12px 16px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    .property-booking-block__header {
      min-height: auto;
      flex: 1;
    }
    .dates-selection {
      display: none;
    }
    .visitors-selection {
      display: none;
    }
  }
  .claim-block {
    display: none;
  }
}

.checkout-button.loading {
  background-color: var(--grey); /* Example: change to a grey color */
  cursor: not-allowed;
}

/* Assuming you have an icon with class `.icon-loading` for visual indication */
.icon-loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
